<template>
    <div>
        <div ref="imageSliderEl"
            class="slider-root flex overflow-x-scroll   select-none cursor-pointer  touch-pan-y touch-pinch-zoom mb-3">
            <template v-for="(item, i) in props.items" :key="i">
                <div class="min-w-full max-w-full overflow-hidden transition-all" :style="{
                    maxHeight: maxHeight,
                    maxWidth: imgSliderBounding.width.value
                }">
                    <img v-show="allowedIndexList.includes(i)" :src="item.src"
                        class="min-w-full drag overflow-hidden max-w-full object-contain" draggable="false">
                </div>
            </template>
        </div>
        <div class="flex justify-center">
            <div class="flex gap-1 flex-wrap">
                <div v-for="(_, i) in (props.items?.length ?? 0)" :key="i" class="h-3 w-3  rounded-full" :class="{
                    ['bg-stone-300']: currentIndexByScrollX != i,
                    ['bg-red-600']: currentIndexByScrollX == i,
                }">
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import type { imgInfo } from '@/types/book_item';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useScroll, useElementBounding, useMouse, useMousePressed } from '@vueuse/core';

const props = defineProps<{
    items?: imgInfo[]
}>()
const emit = defineEmits<{
    (e: 'event:imageClicked', index: number): void
}>()

const imgSliderEl = useTemplateRef('imageSliderEl')

const { x: mouseX, y: mouseY, } = useMouse({ target: imgSliderEl, touch: true })

const imgSliderBounding = useElementBounding(imgSliderEl)
const { x } = useScroll(imgSliderEl, {
    behavior: 'smooth'
})

const currIndex = ref(0)

type Coordinate = {
    x: number
    y: number
}


let resizeTimeout: number;
watch(x, () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(() => {
        setMaxHeight()
    }, 200);
})

const mouseTouched = ref<{
    scrollStart?: number
    start?: Coordinate,
    end?: Coordinate,
    touched?: boolean,
    swiped?: boolean,
    sourceType: string | null,
}>({
    sourceType: null
})

const { pressed, sourceType } = useMousePressed({ target: imgSliderEl })

const allowedIndexList = computed(() => {
    const items = [currentIndexByScrollX.value];
    if (currentIndexByScrollX.value > 0) {
        items.push(currentIndexByScrollX.value - 1)
    }
    if ((props.items?.length ?? 0) >= currentIndexByScrollX.value + 2) {
        items.push(currentIndexByScrollX.value + 1)
    }
    return items
})

const maxHeight = ref(`200px`)

const currentIndexByScrollX = computed(() => {
    if (x.value < imgSliderBounding.width.value) {
        return 0
    }

    return Math.round(x.value / imgSliderBounding.width.value)
})

const setMaxHeight = () => {
    if (!props.items) {
        return
    }
    let cI = currentIndexByScrollX.value

    const currImg = props.items[cI]

    maxHeight.value = `${(imgSliderBounding.width.value * currImg.height) / currImg.width}px`
}

onMounted(() => {
    setMaxHeight()
})

watch(pressed, (newV, oldV) => {
    if (!oldV && newV) {
        mouseTouched.value.start = {
            x: mouseX.value,
            y: mouseY.value
        };
        mouseTouched.value.scrollStart = x.value
        mouseTouched.value.touched = true
        mouseTouched.value.swiped = false
        mouseTouched.value.sourceType = sourceType.value
        return
    } else if (!newV && oldV) {

        if (mouseTouched.value.start?.x && mouseTouched.value.start?.y) {
            const mvX = mouseTouched.value.start.x - mouseX.value
            const mvY = mouseTouched.value.start.y - mouseY.value
            const allowedDiffForImageClicked = 2

            if ((mvY >= 0 && mvY < allowedDiffForImageClicked) || (mvY <= 0 && mvY > -allowedDiffForImageClicked)) {
                if ((mvX >= 0 && mvX < allowedDiffForImageClicked) || (mvX <= 0 && mvX > -allowedDiffForImageClicked)) {
                    emit('event:imageClicked', currentIndexByScrollX.value)
                }
            }
        }


        mouseTouched.value.start = {
            x: mouseX.value,
            y: mouseY.value
        };
        mouseTouched.value.touched = false


    }

})

const allowedMVDiffX = 70
const allowedMVDiffY = 20

watch(mouseX, () => {
    if (!mouseTouched.value.touched || !mouseTouched.value.start || mouseTouched.value.swiped) {
        return
    }
    const mvX = mouseTouched.value.start.x - mouseX.value
    const mvY = mouseTouched.value.start.y - mouseY.value

    if (mvY > allowedMVDiffY) {
        return;
    }

    if (mvX > allowedMVDiffX) {
        if ((props.items?.length ?? 0) < currIndex.value + 2) {

            return
        }
        setCurrIndex(currIndex.value + 1)
        mouseTouched.value.swiped = true
    } else if (mvX < -allowedMVDiffX) {
        if (0 > (currIndex.value - 1)) {

            return
        }
        setCurrIndex(currIndex.value - 1)
        mouseTouched.value.swiped = true
    }
}, {
    deep: true
})


const setCurrIndex = (cI: number) => {
    currIndex.value = cI
    x.value = getElXCoordinate(cI)


}

const getElXCoordinate = (index: number) => {
    if (index == 0) {
        return 0
    } else if (!imgSliderEl.value) {
        return 0
    }
    return Math.ceil((index * imgSliderBounding.width.value))
}


</script>
<style scoped lang="sass">
.slider-root
    overflow: auto
    -ms-overflow-style: none
    scrollbar-width: none
.hide-scrollbar::-webkit-scrollbar
    display: none
</style>